// k:env-boundary — the executor composes the claude child's environment,
// injecting the customer's ANTHROPIC_API_KEY and a per-job CLAUDE_CONFIG_DIR
// on the hosted tier (BE-005).
import { ChildProcess, spawn } from 'child_process';
import { promises as fs } from 'fs';
import os from 'os';
import path from 'path';
import { StringDecoder } from 'string_decoder';
import type { DispatchedJob } from '@whittlelabs/jobs';
import type { PromptExecutionSpec } from '@whittlelabs/loom';
import {
  renderPromptExecution,
  readPromptExecutionSpec,
  pickProviderHints,
  PROMPT_EXECUTION_STRATEGY,
} from '@whittlelabs/loom';
import { KeepClient, ServiceTokenManager } from '@whittlelabs/keep';
import type {
  CostHint,
  ExecutionContext,
  ExecutorCapability,
  ExecutorResponse,
  JobExecutor,
} from './types';
import { assertOutputValid } from './validate-output';
import { ProgressReporter, StreamJsonTap } from './progress';
import { prepareWorkspace, type PreparedWorkspace } from './workspace';
import { applyOverlay, type OverlayResult } from './overlay';
import { resolveConfigStringOrEnv } from '../config/env-ref';
import { expandHome } from '../branding/apply';

/**
 * Claude-Code executor: spawns the `claude --print` CLI in a working directory,
 * pipes the rendered prompt to stdin, returns stdout as the raw response. It
 * interprets the `prompt-execution` strategy off the opaque payload itself.
 *
 * Two auth modes:
 *  - **Local** (default): the ambient Claude Code login (`~/.claude`) is the
 *    credential; the child env is inherited untouched.
 *  - **Hosted** (`apiKeyFrom: 'job-metadata'`): per dispatch, the customer's
 *    Anthropic key is revealed from Keep and injected as `ANTHROPIC_API_KEY`
 *    into the child env only, alongside a per-job `CLAUDE_CONFIG_DIR` for
 *    shared-pod isolation. A private-repo workspace is cloned with the
 *    customer's revealed GitHub token.
 */
interface ClaudeCodeConfig {
  /**
   * Base dir for each job's throwaway checkout / scratch dir. Defaults to
   * the OS temp dir; set only when the default temp is unsuitable (small,
   * slow, wrong volume). Every job runs in a fresh subdir removed on
   * settle — no long-lived working directory, no host repo touched.
   */
  tmpDir?: string;
  model?: string;
  maxTurns?: number;
  allowedTools?: string[];
  timeout?: number;
  capabilityId: string;
  /** Present only in hosted (dynamic-key) mode. */
  resolveCredentials?: CredentialResolver;
  /**
   * Origins the executor may fetch workspace-overlay files from (SSRF guard).
   * Empty/unset disables overlays entirely — a secure default; the operator
   * opts in by naming the producer's service (e.g. the Sift API host).
   */
  overlayAllowedHosts?: string[];
}

/** Per-dispatch credentials revealed for the hosted (managed) tier. */
interface DispatchCredentials {
  anthropicApiKey: string;
  /** Present only when a workspace is requested and the customer has a usable GitHub token. */
  githubToken?: string;
}

/**
 * Reveals a customer's credentials for one dispatch. `needGithubToken` gates
 * the (best-effort) GitHub reveal so we decrypt only what the job will use.
 */
type CredentialResolver = (opts: {
  customerId: string;
  needGithubToken: boolean;
}) => Promise<DispatchCredentials>;

const DEFAULT_TIMEOUT = 600_000;

class ClaudeCodeExecutor implements JobExecutor {
  readonly capability: ExecutorCapability;
  readonly strategies = [PROMPT_EXECUTION_STRATEGY];
  private readonly config: ClaudeCodeConfig;

  constructor(config: ClaudeCodeConfig) {
    this.config = config;
    this.capability = { id: config.capabilityId };
  }

  estimate(dispatch: DispatchedJob): CostHint {
    const spec = readPromptExecutionSpec(dispatch.inputs);
    return {
      inputTokens: spec.costCapHint?.estimatedInputTokens,
      outputTokens: spec.costCapHint?.estimatedOutputTokens,
    };
  }

  async execute(
    dispatch: DispatchedJob,
    signal: AbortSignal,
    ctx?: ExecutionContext,
  ): Promise<ExecutorResponse> {
    const { prompt, spec } = renderPromptExecution(dispatch.inputs);

    // Hosted (managed) tier: reveal the customer's credentials for THIS
    // dispatch only. `customerId` rides the `anthropicApi` hint namespace,
    // which Sift sets for hosted routing regardless of executor.
    let credentials: DispatchCredentials | null = null;
    if (this.config.resolveCredentials) {
      const hints = pickProviderHints(spec, 'anthropic-api') as { customerId?: string };
      const customerId = typeof hints.customerId === 'string' ? hints.customerId : undefined;
      if (!customerId) {
        throw new Error(
          'claude-code dynamic-key mode requires providerHints.anthropicApi.customerId on every job',
        );
      }
      credentials = await this.config.resolveCredentials({
        customerId,
        needGithubToken: !!spec.workspace,
      });
    }

    // Shared-pod isolation (hosted only): a per-job CLAUDE_CONFIG_DIR keeps
    // Claude's transcripts/session state from bleeding across customers on a
    // shared pod. In LOCAL mode we must NOT override it — the ambient
    // `~/.claude` login is the auth source, and repointing it at an empty dir
    // would break sign-in.
    let jobScratch: string | null = null;
    let configDir: string | undefined;
    if (credentials) {
      jobScratch = await fs.mkdtemp(path.join(os.tmpdir(), 'shuttle-cc-'));
      configDir = path.join(jobScratch, 'claude-config');
      await fs.mkdir(configDir);
    }

    // When the spec requests a workspace, run the model inside a checkout at
    // the exact head SHA so it can explore the repo under review. A private
    // repo is fetched with the customer's revealed GitHub token. Cleanup is
    // unconditional — success, failure, timeout, abort.
    const tmpBase = this.config.tmpDir ? expandHome(this.config.tmpDir) : os.tmpdir();
    let workspace: PreparedWorkspace | null = null;
    // A prompt-only job (no workspace) still needs a directory to spawn the
    // CLI in; give it a throwaway scratch dir rather than any host path.
    let promptOnlyScratch: string | null = null;
    let overlayResult: OverlayResult | null = null;
    // Live-progress sink: engaged only when the product set a routing channel
    // AND the claim loop supplied a reporter. Absent ⇒ zero progress traffic.
    const reporter =
      spec.progressChannel && ctx?.reportProgress
        ? new ProgressReporter((frames) => ctx.reportProgress!(spec.progressChannel!, frames))
        : null;
    try {
      if (spec.workspace) {
        workspace = await prepareWorkspace(spec.workspace, {
          signal,
          tmpDir: tmpBase,
          ...(credentials?.githubToken ? { gitToken: credentials.githubToken } : {}),
        });
        // Drop any referenced overlay files (e.g. team standards) into the
        // prepared run dir so the model can read them as ordinary files. They
        // live inside the workspace and are cleaned up with it.
        if (spec.workspace.overlay) {
          overlayResult = await applyOverlay(workspace.cwd, spec.workspace.overlay, {
            allowedHosts: this.config.overlayAllowedHosts ?? [],
            signal,
          });
        }
      }
      const childEnv = buildChildEnv(credentials, configDir);
      // Per-job model override: `providerHints.claudeCode.model` wins over
      // the static YAML model, so one running Sifter serves every offered
      // model (hosted multi-model configurations ride this).
      const claudeHints = pickProviderHints(spec, 'claude-code') as { model?: unknown };
      const hintedModel =
        typeof claudeHints.model === 'string' && claudeHints.model.length > 0
          ? claudeHints.model
          : undefined;
      if (!workspace) {
        promptOnlyScratch = await fs.mkdtemp(path.join(tmpBase, 'shuttle-cc-run-'));
      }
      const runCwd = workspace?.cwd ?? promptOnlyScratch!;
      const { response, readPaths, toolText } = await this.runClaude(
        prompt,
        spec,
        runCwd,
        childEnv,
        signal,
        hintedModel,
        reporter,
      );
      if (workspace) {
        // Deterministic exploration telemetry: every workspace file the model
        // opened (Read tool calls off the stream-json transcript), cwd-relative.
        // Overlaid files (e.g. team standards) are frequently opened via the
        // shell (`cat`, `grep`) rather than the Read tool — the Read-only set
        // undercounts them (an unfixed `cat .sift/standards/x.md` read as "0
        // read") — so also credit any overlaid file whose path appears in a
        // tool call's input. Products consume this to show what a review
        // consulted — e.g. which standards docs were read.
        const reads = [...readPaths];
        for (const rel of overlayResult?.writtenPaths ?? []) {
          if (toolText.includes(rel)) reads.push(path.join(runCwd, rel));
        }
        response.outputs.push({
          label: 'workspace_reads',
          content: { paths: relativeWorkspaceReads(reads, runCwd) },
        });
        // Structured audit trail: reviewers and operators can see whether a
        // given review actually explored the checkout or fell back to the
        // diff (and why — including a failed private-repo auth), plus which
        // overlay files landed.
        response.outputs.push({
          label: 'workspace',
          content: {
            materialized: workspace.materialized,
            ...(workspace.warning ? { warning: workspace.warning } : {}),
            ...(overlayResult
              ? {
                  overlay: {
                    written: overlayResult.written,
                    ...(overlayResult.skipped.length > 0 ? { skipped: overlayResult.skipped } : {}),
                  },
                }
              : {}),
          },
        });
      }
      return response;
    } finally {
      reporter?.close();
      if (workspace) await workspace.cleanup();
      if (promptOnlyScratch) await fs.rm(promptOnlyScratch, { recursive: true, force: true }).catch(() => {});
      if (jobScratch) await fs.rm(jobScratch, { recursive: true, force: true }).catch(() => {});
    }
  }

  private runClaude(
    prompt: string,
    spec: PromptExecutionSpec,
    cwd: string,
    childEnv: NodeJS.ProcessEnv | undefined,
    signal: AbortSignal,
    modelOverride?: string,
    reporter?: ProgressReporter | null,
  ): Promise<{ response: ExecutorResponse; readPaths: string[]; toolText: string }> {
    const outputLabel = spec.outputLabel;
    const model = modelOverride ?? this.config.model;

    return new Promise<{ response: ExecutorResponse; readPaths: string[]; toolText: string }>((resolve, reject) => {
      // stream-json gives us the per-turn tool telemetry (which files the
      // model Read — the deterministic source for exploration reporting)
      // plus the terminal result event with real token usage; --verbose is
      // required for streaming under --print.
      const args = ['--print', '--output-format', 'stream-json', '--verbose'];
      // Structured output: hand the spec's schema to the CLI so the final
      // answer is schema-guaranteed upstream (constrained decoding) instead
      // of validated after the fact. A malformed hand-rolled JSON emission
      // cost three six-minute Opus attempts on 2026-07-13; delegation makes
      // that class impossible. Requires a CLI that knows --json-schema.
      if (
        spec.outputSchema &&
        typeof spec.outputSchema === 'object' &&
        Object.keys(spec.outputSchema).length > 0
      ) {
        args.push('--json-schema', JSON.stringify(spec.outputSchema));
      }

      if (model) args.push('--model', model);
      if (this.config.maxTurns !== undefined) args.push('--max-turns', String(this.config.maxTurns));
      if (this.config.allowedTools && this.config.allowedTools.length > 0) {
        args.push('--allowedTools', this.config.allowedTools.join(','));
      }

      let child: ChildProcess;
      try {
        child = spawn('claude', args, {
          cwd,
          stdio: ['pipe', 'pipe', 'pipe'],
          // Undefined in local mode → inherit the ambient env (and its
          // `~/.claude` login) unchanged.
          ...(childEnv ? { env: childEnv } : {}),
        });
      } catch (err) {
        reject(err);
        return;
      }

      const stdoutChunks: Buffer[] = [];
      let settled = false;
      const settle = (fn: () => void): void => {
        if (settled) return;
        settled = true;
        fn();
      };

      // Live-progress tap: tee each stdout chunk into an incremental stream-json
      // reader that feeds the reporter. A StringDecoder guards multibyte chars
      // split across chunk boundaries. The authoritative full-buffer parse below
      // is untouched — this path is side-effect-only and best-effort.
      const tap = reporter
        ? new StreamJsonTap({
            onReasoning: (t) => reporter.reasoning(t),
            onTool: (name, target) => reporter.tool(name, target),
            onResult: () => reporter.flush(),
          })
        : null;
      const decoder = tap ? new StringDecoder('utf8') : null;

      child.stdout!.on('data', (chunk: Buffer) => {
        stdoutChunks.push(chunk);
        if (tap && decoder) tap.push(decoder.write(chunk));
      });
      child.stdin!.write(prompt);
      child.stdin!.end();

      const timeout = this.config.timeout ?? DEFAULT_TIMEOUT;
      const timer = setTimeout(() => {
        child.kill('SIGTERM');
        settle(() => reject(new Error(`Claude Code timed out after ${timeout}ms`)));
      }, timeout);

      function onAbort(): void {
        child.kill('SIGTERM');
        settle(() => reject(new Error('Dispatch aborted')));
      }

      if (signal.aborted) {
        clearTimeout(timer);
        child.kill('SIGTERM');
        settle(() => reject(new Error('Dispatch aborted')));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });

      child.on('error', (err: Error) => {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
        settle(() => reject(err));
      });

      child.on('close', (code: number | null) => {
        clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
        if (tap && decoder) {
          tap.push(decoder.end());
          tap.end();
        }
        const stdout = Buffer.concat(stdoutChunks).toString('utf-8');
        if (code === 0) {
          // Parse/validation problems are ATTEMPT failures, never process
          // failures: a throw inside this event callback is uncatchable
          // upstream and took down the whole Sifter (2026-07-09 — a
          // superseded run's child exited 0 with empty stdout, and the
          // schema-validation throw here killed the subscribe loop).
          try {
            // Prefer the stream's terminal result event; a transcript that
            // somehow lacks one (older CLI, unexpected shape) degrades to
            // treating the whole stdout as the answer, exactly as before.
            const stream = parseStreamJson(stdout);
            // A zero-exit run can still self-report failure (is_error on the
            // terminal event). Name it — the alternative is a downstream
            // schema-validation error that hides the real cause.
            if (stream.resultMeta?.isError) {
              throw new Error(
                `Claude Code reported ${stream.resultMeta.subtype ?? 'an error'} on its terminal event`,
              );
            }
            const answerText = stream.finalText ?? stdout;
            // The CLI's structured output is schema-guaranteed upstream;
            // text extraction remains the fallback for runs without a
            // schema (or a CLI predating --json-schema).
            const parsed = stream.structuredOutput ?? extractJsonObject(answerText);
            if (parsed === undefined && stream.resultMeta) {
              // The final message parsed as prose with no JSON object. Say so
              // with a snippet — "value expected type object, got null" from
              // schema validation explains nothing to the person re-running.
              throw new Error(
                `claude's final message contained no JSON object (result subtype=${stream.resultMeta.subtype ?? 'unknown'}; ` +
                  `message head: ${JSON.stringify(answerText.slice(0, 160))})`,
              );
            }
            assertOutputValid(spec, parsed);
            settle(() =>
              resolve({
                response: {
                  outputs: [{ label: outputLabel, content: parsed ?? { raw: answerText } }],
                  rawText: answerText,
                  parsed,
                  usage: {
                    aiProvider: 'claude-code',
                    ...(model ? { aiModel: model } : {}),
                    ...(stream.usage ?? {}),
                  },
                },
                readPaths: stream.readPaths,
                toolText: stream.toolText,
              }),
            );
          } catch (err) {
            settle(() => reject(err instanceof Error ? err : new Error(String(err))));
          }
        } else {
          settle(() => reject(new Error(`Claude Code exited with code ${code}`)));
        }
      });
    });
  }
}

/**
 * Optional dependency hooks for the dynamic-key path. Production uses the real
 * managers; tests inject stubs so they don't mock global fetch for Keep calls.
 * Mirrors `AnthropicApiDeps`.
 */
export interface ClaudeCodeDeps {
  buildServiceTokenManager?: (config: {
    keepApiUrl: string;
    clientId: string;
    clientSecret: string;
  }) => Pick<ServiceTokenManager, 'getToken'>;
  buildKeepClient?: (config: {
    baseUrl: string;
    serviceToken: string;
  }) => Pick<KeepClient, 'revealManagedSifterKey' | 'getUserGithubToken'>;
}

export function createClaudeCodeExecutor(
  config: Record<string, unknown>,
  capabilityId = 'claude-code',
  deps: ClaudeCodeDeps = {},
): ClaudeCodeExecutor {
  // No working directory is configured any more: every job runs in a fresh
  // throwaway checkout / scratch dir. `cwd` in an existing YAML is ignored
  // (harmless leftover from before 2026-07-13). `tmpDir` optionally moves
  // the throwaway root off the default OS temp dir; `~` is expanded at use.
  const validated: ClaudeCodeConfig = { capabilityId };
  if (config.tmpDir !== undefined) {
    if (typeof config.tmpDir !== 'string' || config.tmpDir.length === 0) {
      throw new Error('"tmpDir" must be a non-empty string');
    }
    validated.tmpDir = config.tmpDir;
  }

  if (config.model !== undefined) {
    if (typeof config.model !== 'string') throw new Error('"model" must be a string');
    validated.model = config.model;
  }
  if (config.maxTurns !== undefined) {
    if (typeof config.maxTurns !== 'number' || !Number.isInteger(config.maxTurns) || config.maxTurns < 1) {
      throw new Error('"maxTurns" must be a positive integer');
    }
    validated.maxTurns = config.maxTurns;
  }
  if (config.allowedTools !== undefined) {
    if (!Array.isArray(config.allowedTools) || !config.allowedTools.every((t) => typeof t === 'string')) {
      throw new Error('"allowedTools" must be an array of strings');
    }
    validated.allowedTools = config.allowedTools as string[];
  }
  if (config.timeout !== undefined) {
    if (typeof config.timeout !== 'number' || config.timeout <= 0) {
      throw new Error('"timeout" must be a positive number');
    }
    validated.timeout = config.timeout;
  }
  if (config.apiKeyFrom !== undefined) {
    validated.resolveCredentials = buildCredentialResolver(config, deps);
  }
  if (config.overlayAllowedHosts !== undefined) {
    if (
      !Array.isArray(config.overlayAllowedHosts) ||
      !config.overlayAllowedHosts.every((h) => typeof h === 'string')
    ) {
      throw new Error('"overlayAllowedHosts" must be an array of strings');
    }
    validated.overlayAllowedHosts = config.overlayAllowedHosts as string[];
  }

  return new ClaudeCodeExecutor(validated);
}

/**
 * Assemble the claude child's environment for the hosted tier: the ambient
 * env plus the customer's revealed key and a per-job config dir. Returns
 * undefined in local mode so the child inherits the ambient env (and its
 * `~/.claude` login) untouched. The key lives ONLY in this child env — never
 * argv, never on disk, never logged.
 */
function buildChildEnv(
  credentials: DispatchCredentials | null,
  configDir: string | undefined,
): NodeJS.ProcessEnv | undefined {
  if (!credentials && !configDir) return undefined;
  return {
    ...process.env,
    ...(credentials ? { ANTHROPIC_API_KEY: credentials.anthropicApiKey } : {}),
    ...(configDir ? { CLAUDE_CONFIG_DIR: configDir } : {}),
  };
}

/**
 * Build the per-dispatch credential resolver for hosted mode. Reads the Keep
 * service credentials (config or env, like the anthropic-api executor),
 * exchanges a service token, and reveals the customer's Anthropic key
 * (required) plus — only when a workspace is requested — their GitHub token
 * (best-effort: a failure degrades the workspace to prompt-only via the audit
 * row, it does not fail the review).
 */
function buildCredentialResolver(
  config: Record<string, unknown>,
  deps: ClaudeCodeDeps,
): CredentialResolver {
  if (config.apiKeyFrom !== 'job-metadata') {
    throw new Error(
      `Unsupported "apiKeyFrom" value: ${JSON.stringify(config.apiKeyFrom)}. Only "job-metadata" is supported.`,
    );
  }
  const keepApiUrl = resolveConfigStringOrEnv(config, 'keepApiUrl', 'KEEP_API_URL');
  const clientId = resolveConfigStringOrEnv(config, 'keepClientId', 'KEEP_CLIENT_ID');
  const clientSecret = resolveConfigStringOrEnv(config, 'keepClientSecret', 'KEEP_CLIENT_SECRET');

  const stmFactory = deps.buildServiceTokenManager ?? ((cfg) => new ServiceTokenManager(cfg));
  const stm = stmFactory({ keepApiUrl, clientId, clientSecret });
  const keepClientFactory = deps.buildKeepClient ?? ((cfg) => new KeepClient(cfg));

  return async ({ customerId, needGithubToken }) => {
    const serviceToken = await stm.getToken();
    if (!serviceToken) {
      throw new Error(
        'claude-code dynamic-key mode failed to exchange a Keep service token. Check KEEP_CLIENT_ID / KEEP_CLIENT_SECRET.',
      );
    }
    const keep = keepClientFactory({ baseUrl: keepApiUrl, serviceToken });
    const reveal = await keep.revealManagedSifterKey(customerId);
    const credentials: DispatchCredentials = { anthropicApiKey: reveal.apiKey };
    if (needGithubToken) {
      try {
        const gh = await keep.getUserGithubToken(customerId);
        credentials.githubToken = gh.access_token;
      } catch {
        // Best-effort. A missing/failed GitHub token means a private repo
        // won't clone, so the workspace degrades to prompt-only — surfaced on
        // the `workspace` audit row, not a hard failure. Swallowed WITHOUT
        // logging the error, which could reference the customer.
      }
    }
    return credentials;
  };
}

/**
 * Parse a `--output-format stream-json` transcript: one JSON event per
 * line. We extract the terminal `result` event (the final answer text +
 * real token usage) and every `Read` tool call's file path. Unparseable
 * lines are skipped — the caller falls back to raw-stdout handling when
 * no result event is found.
 */
export function parseStreamJson(stdout: string): {
  finalText: string | null;
  readPaths: string[];
  usage: { inputTokens?: number; outputTokens?: number; costUsd?: number } | null;
  /** Terminal-event facts for error reporting; null when no result event. */
  resultMeta: { subtype: string | null; isError: boolean } | null;
  /** Schema-conforming object from --json-schema runs; null otherwise. */
  structuredOutput: Record<string, unknown> | null;
  /** Concatenated JSON of every tool_use input — for attributing reads that did
   *  not go through the Read tool (a `cat`/`grep` inside a Bash command). */
  toolText: string;
} {
  let finalText: string | null = null;
  const readPaths: string[] = [];
  let usage: { inputTokens?: number; outputTokens?: number; costUsd?: number } | null = null;
  let resultMeta: { subtype: string | null; isError: boolean } | null = null;
  let structuredOutput: Record<string, unknown> | null = null;
  let toolText = '';

  for (const line of stdout.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('{')) continue;
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      continue;
    }
    if (event.type === 'assistant') {
      const content = (event.message as { content?: unknown } | undefined)?.content;
      if (!Array.isArray(content)) continue;
      for (const block of content) {
        const b = block as { type?: string; name?: string; input?: Record<string, unknown> };
        if (b?.type !== 'tool_use') continue;
        const fp = b.input?.file_path;
        if (b.name === 'Read' && typeof fp === 'string') {
          readPaths.push(fp);
        }
        // Accumulate every tool call's input so the caller can attribute reads
        // that didn't go through the Read tool (e.g. `cat`/`grep` in a Bash
        // command) — the Read-only telemetry would miss an overlaid doc `cat`ed.
        if (b.input && typeof b.input === 'object') toolText += JSON.stringify(b.input);
      }
    } else if (event.type === 'result') {
      // An empty result string is as useless as an absent one; treating
      // it as text would guarantee a JSON-extraction failure downstream.
      if (typeof event.result === 'string' && event.result.trim().length > 0) {
        finalText = event.result;
      }
      resultMeta = {
        subtype: typeof event.subtype === 'string' ? event.subtype : null,
        isError: event.is_error === true,
      };
      const so = event.structured_output;
      if (so && typeof so === 'object' && !Array.isArray(so)) {
        structuredOutput = so as Record<string, unknown>;
      }
      const u = event.usage as { input_tokens?: number; output_tokens?: number } | undefined;
      const cost = event.total_cost_usd;
      usage = {
        ...(typeof u?.input_tokens === 'number' ? { inputTokens: u.input_tokens } : {}),
        ...(typeof u?.output_tokens === 'number' ? { outputTokens: u.output_tokens } : {}),
        ...(typeof cost === 'number' ? { costUsd: cost } : {}),
      };
    }
  }
  return { finalText, readPaths, usage, resultMeta, structuredOutput, toolText };
}

const MAX_REPORTED_READS = 2000;

/**
 * Normalize raw Read paths to sorted, deduped, cwd-relative form,
 * dropping anything outside the workspace (absolute paths into the
 * ambient machine say nothing about the checkout).
 */
export function relativeWorkspaceReads(readPaths: string[], cwd: string): string[] {
  const root = path.resolve(cwd);
  const out = new Set<string>();
  for (const p of readPaths) {
    const resolved = path.resolve(root, p);
    if (resolved === root) continue;
    if (!resolved.startsWith(root + path.sep)) continue;
    out.add(resolved.slice(root.length + 1));
    if (out.size >= MAX_REPORTED_READS) break;
  }
  return [...out].sort();
}

/**
 * Extract the review JSON object from claude's final message. The prompt
 * demands bare JSON, but a multi-turn exploration run (workspace mode) makes
 * the model measurably more likely to wrap its answer in prose or a
 * ```json fence — requiring byte-pure JSON turned those runs into attempt
 * failures (2026-07-11).
 *
 * Strategy: exact parse first, then scan every top-level balanced `{...}`
 * (string/escape aware, so braces inside JSON strings don't fool it) and
 * return the LAST one that parses. Last, not first: a `--print` transcript
 * ends with the answer, and exploration runs sometimes quote a small JSON
 * fragment mid-narration ("the schema is `{...}`, so…") that a first-match
 * scan would wrongly return. Undefined when nothing parses; schema
 * validation then fails the attempt as before.
 */
export function extractJsonObject(s: string): Record<string, unknown> | undefined {
  const exact = tryParseObject(s);
  if (exact) return exact;

  let last: Record<string, unknown> | undefined;
  let i = 0;
  while (i < s.length) {
    if (s[i] !== '{') {
      i++;
      continue;
    }
    let depth = 0;
    let inString = false;
    let escaped = false;
    let end = -1;
    for (let j = i; j < s.length; j++) {
      const ch = s[j];
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = inString;
        continue;
      }
      if (ch === '"') {
        inString = !inString;
        continue;
      }
      if (inString) continue;
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) {
          end = j;
          break;
        }
      }
    }
    if (end === -1) break; // unbalanced tail; no further top-level object
    const candidate = tryParseObject(s.slice(i, end + 1));
    if (candidate) last = candidate;
    i = end + 1; // resume after this top-level object, never inside it
  }
  return last;
}

function tryParseObject(s: string): Record<string, unknown> | undefined {
  try {
    const parsed = JSON.parse(s);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}
